/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { ImportCommitDone, SpreadsheetEntity } from '../types';

/**
 * What the result strip needs to word itself.
 *
 * It travels in react-router navigation state rather than in the URL: the
 * file name is not an addressable fact, and putting
 * them in query params would make a shared link claim an import that the
 * person opening it never ran.
 */
export interface ImportResultSummary {
  importId: string;
  entity: SpreadsheetEntity;
  fileName: string;
  /** Records that actually landed: created plus completed. */
  imported: number;
  status: 'done' | 'processing';
  /** False once the 24 h undo window has passed, or while still processing. */
  undoable: boolean;
}

/** The key the import screen writes its summary under when it navigates away. */
export const IMPORT_SUMMARY_STATE_KEY = 'spreadsheetImportSummary';


/**
 * Turns a commit result into the summary the listing's strip renders.
 *
 * @param result - What the commit endpoint answered.
 * @param entity - Which listing the records landed in.
 * @param fileName - The spreadsheet's name, for the sentence.
 * @returns The summary to carry across the navigation.
 */
export function toResultSummary(
  result: ImportCommitDone,
  entity: SpreadsheetEntity,
  fileName: string,
): ImportResultSummary {
  return {
    importId: result.import_id,
    entity,
    fileName,
    imported: result.created + result.merged,
    status: result.status,
    // A finished import can be taken back for 24 h. The backend disables the
    // records the commit created; records it merely completed stay completed,
    // which is what the confirmation dialog says out loud.
    undoable: result.status === 'done',
  };
}

/**
 * Reads a summary out of react-router's location state.
 *
 * A refresh drops the state, which is why the caller also strips `?import`
 * when this returns `null`: a strip with no numbers in it is worse than no
 * strip.
 *
 * @param state - `useLocation().state`.
 * @param importId - The id currently in `?import=`.
 * @returns The summary when it belongs to this import, otherwise `null`.
 */
export function readResultSummary(state: unknown, importId: string | null): ImportResultSummary | null {
  if (!importId || !isRecord(state)) return null;
  const summary = state[IMPORT_SUMMARY_STATE_KEY];
  if (!isRecord(summary)) return null;
  const candidate = summary as unknown as ImportResultSummary;
  return candidate.importId === importId ? candidate : null;
}

/**
 * Whether a value is a non-null object, as a guard the caller can narrow on.
 *
 * Navigation state is `unknown` twice over -- the location's own state, and
 * then whatever sits under the key -- and the same duck-type check was
 * written out at both depths.
 *
 * @param value - The value to narrow.
 * @returns Whether it is a non-null object.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object';
}
